import React, { useCallback, useEffect, useState } from "react";
import logger from "../../../../../utils/logger";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { fbChangeGroupRegistrationCode } from "../../../../../services/firebaseService/endPoints/admin/groupConfig";
import { generateQR } from "../../../../../helperFunctions/util";
import html2canvas from "html2canvas";
import { DescriptionText } from "../../../../../CommonComponents";
import ModernOnboardOptions from "./ModernOnboardOptions/ModernOnboardOptions";
import MemberListUploader from "./MemberListUploader/MemberListUploader";
import "./OnboardOptions.css";
import { refreshGroupData } from "../../../../../redux/actions/adminData";
import { toast } from "react-toastify";

const SIZE = 150;

const OnboardOptions = ({ handleTabChange }) => {

  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [isLoading, setIsLoading] = useState(false);
  const [registrationCode, setRegistrationCode] = useState("");
  const [registrationLink, setRegistrationLink] = useState(null);
  const [editStatus, setEditStatus] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [svg, setSvg] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [isRoleSaving, setIsRoleSaving] = useState(false);

  const resetState = useCallback(() => {
    setShowModal(false);
    setEditStatus(false);
    setShowUploadModal(false);
    setIsRoleSaving(false);
    setIsLoading(false);
    setRegistrationCode("");
    setRegistrationLink(null);
    setSvg("");
  }, []);

  useEffect(() => {
    if (groupData?.id) {
      resetState();
      setRegistrationLink(groupData?.registrationLink);
      setRegistrationCode(groupData?.registrationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const handleChangeRegistrationCode = async () => {
    try {
      if (!groupData?.id) {
        toast.error("Group information not available");
        return;
      }

      setIsLoading(true);
      const res = await fbChangeGroupRegistrationCode(
        groupData?.id,
        groupData?.regCodeRole || "member",
      );

      if (res.success) {
        const { link, code } = res.data;

        const newQrCode = await generateQR(
          link,
          groupData?.configuration?.logo,
          SIZE,
        );

        setRegistrationCode(code);
        setRegistrationLink(link);
        setSvg(newQrCode);
        toast.success("Registration code updated successfully");
      }
    } catch (error) {
      toast.error("Error in updating registration code");
      logger.error(`Error in changing registration code ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const saveRegCodeRole = async (regCodeRole) => {
    try {
      setIsRoleSaving(true);

      if (regCodeRole || (!groupData?.registrationLink && registrationCode)) {
        const res = await fbChangeGroupRegistrationCode(
          groupData?.id,
          regCodeRole,
          registrationCode,
        );

        if (res.success) {
          const newSvg = await generateQR(
            res.data.link,
            groupData?.configuration?.logo,
            SIZE,
          );
          setRegistrationLink(res.data.link);
          setSvg(newSvg);

          await refreshGroupData(groupData?.id);
        }
      } else if (!registrationCode) {
        await handleChangeRegistrationCode();
      }
    } catch (error) {
      logger.error(`Error in saving reg code role ${error}`);
    } finally {
      setIsRoleSaving(false);
    }
  };

  const downloadURI = useCallback((uri, filename) => {
    let xhr = new XMLHttpRequest();
    xhr.responseType = "blob";
    xhr.onload = () => {
      let blob = xhr.response;
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    };
    xhr.open("GET", uri);
    xhr.send();
  }, []);

  const downloadAsImage = useCallback(
    (id) => {
      let htmlElem = document.getElementById(id);
      html2canvas(htmlElem).then((canvas) => {
        let imageUrl = canvas.toDataURL("image/png");
        downloadURI("data:" + imageUrl, "QRCode.png");
      });
    },
    [downloadURI],
  );

  const openUploadModal = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
  }, []);

  useEffect(() => {
    generateQR(
      groupData?.registrationLink,
      groupData?.configuration?.logo,
      SIZE,
    ).then((newSvg) => {
      setSvg(newSvg);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  return (
    <div>
      <div style={{ marginBottom: "2px" }}>
        <DescriptionText showBackground={true}>
          <div>
            <p>
              Control group access by uploading member emails (options 1, 2) or
              selling membership packages (option 3). Have member data to import
              (address, shirt size, etc)? Email{" "}
              <a href="mailto:support@chant.fan">support@chant.fan</a>.
            </p>
            <p>
              For &apos;open&apos; groups, share a QR code / link (option 4).
              Set for members (full access) or &apos;non-members&apos; (limited
              app permissions). This provides open access, so share carefully.
            </p>
            <p>
              <span style={{ color: "#ff4040" }}>Important:</span> Chant does
              not email members. Notify members via email, social or sharing the{" "}
              <a href="/admin/members/promote"> Promote</a> image.
            </p>
          </div>
        </DescriptionText>
      </div>
      <div
        style={{
          width: "100%",
          overflow: "auto",
          backgroundColor: "var(--primary-dashboard-background)",
        }}
      >
        <ModernOnboardOptions
          handleTabChange={handleTabChange}
          isRoleSaving={isRoleSaving}
          regCodeRole={groupData?.regCodeRole}
          registrationLink={registrationLink}
          svg={svg}
          isLoading={isLoading}
          showModal={showModal}
          editStatus={editStatus}
          saveRegCodeRole={saveRegCodeRole}
          downloadAsImage={downloadAsImage}
          handleChangeRegistrationCode={handleChangeRegistrationCode}
          onShowModal={openUploadModal}
          onCloseModal={() => {
            setShowModal(false);
            setEditStatus(false);
          }}
          onOpenModal={openModal}
        />

        {showUploadModal && (
          <MemberListUploader
            open={showUploadModal}
            onCancel={closeUploadModal}
          />
        )}
      </div>
    </div>
  );
};

export default OnboardOptions;
